import {
  FeedbackMediaType,
  useWhatsNewModal,
  WhatsNewModal,
} from "@amzn/stencil-react-components/dist/submodules/whats-new";
import { COOKIE_NAMES, getCookie, setCookie } from "common/helpers/cookie-utils";
import { PlaceholderConfig, replacePlaceholders } from "common/helpers/text-placeholder";
import React, { useEffect, useState } from "react";
import { FC } from "react";

import AiForMefeatures from "../../../../public/features/aiforme/features-popup.json";
import softwareFeatures from "../../../../public/features/software/features-popup.json";

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
const DEFAULT_DISPLAY_DAYS = 7;

export enum FEATURE_TYPE {
  AIFORME = "aiforme",
  SOFTWARE = "software",
}
interface MediaItem {
  type: FeedbackMediaType;
  src: string;
  altText: string;
  style: any;
}

interface FeatureItem {
  titleText: string;
  media: MediaItem;
  bodyText: string | string[] | React.ReactNode;
}

interface FeatureJson {
  title: string;
  date?: string;
  startDate?: string;
  endDate?: string;
  image?: string;
  message: string | string[];
  placeholders?: PlaceholderConfig;
}

interface ModalWhatsNewProps {
  type?: FEATURE_TYPE;
}

const IMAGE_LIST: MediaItem[] = [
  {
    type: FeedbackMediaType.IMAGE,
    src: "/img/working-sitting.svg",
    altText: "person siting in desk",
    style: { padding: "16px" },
  },
  {
    type: FeedbackMediaType.IMAGE,
    src: "/img/team-high-five.svg",
    altText: "team high five",
    style: { padding: "16px" },
  },
  {
    type: FeedbackMediaType.IMAGE,
    src: "/img/character-on-bike.svg",
    altText: "person riding a bike",
    style: { padding: "16px" },
  },
];

const getDomain = (type: FEATURE_TYPE) => {
  switch (type) {
    case FEATURE_TYPE.SOFTWARE:
      return softwareFeatures;
    case FEATURE_TYPE.AIFORME:
      return AiForMefeatures;
    default:
      return AiForMefeatures;
  }
};

export const isFeatureActive = (feature: FeatureJson, now: Date): boolean => {
  if (feature.startDate) {
    const start = new Date(feature.startDate);
    const end = feature.endDate
      ? new Date(feature.endDate)
      : new Date(start.getTime() + DEFAULT_DISPLAY_DAYS * DAY_IN_MILLISECONDS);
    return now >= start && now <= end;
  }

  // Backwards compatible: legacy "date" field treated as startDate, display for DEFAULT_DISPLAY_DAYS
  if (feature.date) {
    const start = new Date(feature.date);
    return now >= start && now <= new Date(start.getTime() + DEFAULT_DISPLAY_DAYS * DAY_IN_MILLISECONDS);
  }

  return false;
};

export const ModalWhatsNew: FC<ModalWhatsNewProps> = ({ type = FEATURE_TYPE.AIFORME }) => {
  const [popUpFeatures, setPopUpFeatures] = useState<FeatureItem[]>([]);
  const features = getDomain(type);

  useEffect(() => {
    const now = new Date();
    const filteredFeatures = (features as FeatureJson[])
      .filter(
        (feature) => !getCookie(`${COOKIE_NAMES.WHATS_NEW_DISMISSED}-${feature.title}`) && isFeatureActive(feature, now)
      )
      .map((feature, index) => {
        let bodyText: string | string[] | React.ReactNode = feature.message;

        if (feature.placeholders && typeof feature.message === "string") {
          bodyText = replacePlaceholders(feature.message, feature.placeholders);
        }

        const media = feature.image ? { ...IMAGE_LIST[0], src: feature.image } : IMAGE_LIST[index % IMAGE_LIST.length];

        return {
          media,
          titleText: feature.title,
          bodyText,
        };
      });

    setPopUpFeatures(filteredFeatures);
  }, []);

  const handleClose = () => {
    popUpFeatures.forEach((feature) => {
      setCookie(`${COOKIE_NAMES.WHATS_NEW_DISMISSED}-${feature.titleText}`, "true");
    });
  };

  const { whatsNewModalProps } = useWhatsNewModal({
    isOpen: true,
    onClose: handleClose,
  });

  if (popUpFeatures.length === 0) {
    return null;
  }

  return <WhatsNewModal data-cy="whats-new-modal" cards={popUpFeatures} {...whatsNewModalProps} />;
};
